import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag, Percent, Wrench, ChevronRight, Tag, Loader2 } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';

const CartDrawer = () => {
  const navigate = useNavigate();
  const { cart, cartOpen, setCartOpen, updateCartItem, removeFromCart, loading, applyPromoCode, removePromoCode } = useCart();
  const [promoInput, setPromoInput] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);

  const handleCheckout = () => {
    setCartOpen(false);
    navigate('/checkout');
  };

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setApplyingPromo(true);
    try {
      const result = await applyPromoCode(promoInput.trim());
      console.log('Success:', result.message || 'Code promo appliqué !');
      setPromoInput('');
    } catch (err) {
      console.log('Error:', err.message || 'Code promo invalide');
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleRemovePromo = async () => {
    try {
      await removePromoCode();
      console.log('Success:', 'Code promo retiré');
    } catch (err) {
      console.log('Error:', 'Erreur');
    }
  };

  // Get TVA rate from cart (default 20%)
  const tvaRate = cart.tva_rate ?? 20;
  const hasInstallation = cart.has_installation ?? false;
  const installationConfirmed = cart.installation_confirmed_2_years ?? false;

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-md bg-[#0a0a0a] border-l border-white/5 p-0 flex flex-col"
      >
        <SheetHeader className="p-6 border-b border-white/5">
          <SheetTitle className="text-lg text-white flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-emerald-400" />
            Votre Panier
          </SheetTitle>
          <SheetDescription className="sr-only">
            Gérez les articles de votre panier et procédez au paiement
          </SheetDescription>
        </SheetHeader>

        {/* Cart Items */}
        {cart.items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <ShoppingBag className="w-16 h-16 text-white/10 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Votre panier est vide</h3>
            <p className="text-white/60 text-sm mb-6">
              Découvrez notre gamme de produits de sécurité Ajax Systems
            </p>
            <Button 
              onClick={() => {
                setCartOpen(false);
                navigate('/shop');
              }}
              className="bg-white text-black hover:bg-white/90 rounded-full px-6"
            >
              Voir les produits
            </Button>
          </div>
        ) : (
          <>
            {/* Scrollable Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.items.map((item) => (
                <div 
                  key={`${item.product_id}-${item.color}`} 
                  className="p-3 bg-black rounded-xl border border-white/5"
                >
                  <div className="flex gap-3">
                    <div className="w-16 h-16 bg-white/5 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={item.images?.[item.color] || item.image_url || item.product?.images?.[item.color] || item.product?.image_url} 
                        alt={item.name || item.product?.name}
                        className="w-full h-full object-contain p-1"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/product/${item.product_id}`}
                        onClick={() => setCartOpen(false)}
                        className="font-medium text-white text-sm hover:text-emerald-400 transition-colors line-clamp-2"
                      >
                        {item.name || item.product?.name}
                      </Link>
                      <p className="text-xs text-white/60 capitalize mt-0.5">
                        {item.color === 'black' ? 'Noir' : 'Blanc'} × {item.quantity}
                      </p>
                      <p className="text-emerald-400 font-mono text-sm mt-1">
                        {(item.price || item.product?.price)?.toFixed(2)} €
                      </p>
                    </div>

                    <div className="flex flex-col items-end justify-between">
                      <div className="flex items-center gap-1 bg-white/5 rounded-lg">
                        <button
                          onClick={() => updateCartItem(item.product_id, item.quantity - 1, item.color)}
                          disabled={loading || item.quantity <= 1}
                          className="p-1.5 text-white/60 hover:text-white disabled:opacity-50 transition-colors"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-white text-sm font-medium w-6 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateCartItem(item.product_id, item.quantity + 1, item.color)}
                          disabled={loading}
                          className="p-1.5 text-white/60 hover:text-white transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.product_id, item.color)}
                        disabled={loading}
                        className="text-white/60 hover:text-red-400 transition-colors p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Services attached to this item */}
                  {item.services && item.services.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/5 space-y-1">
                      {item.services.map((service, idx) => (
                        <div key={idx} className="flex justify-between text-xs">
                          <span className="text-white/60">+ {service.name}</span>
                          <span className="text-white font-mono">
                            {service.price?.toFixed(2)} €
                            {service.price_type === 'monthly' && '/mois'}
                            {service.price_type === 'annual' && '/an'}
                          </span>
                        </div>
                      ))}
                      {item.has_installation_confirmed && (
                        <div className="flex items-center gap-1 text-xs text-green-500 mt-1">
                          <Percent className="w-3 h-3" />
                          <span>TVA 10% activée</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Cart Footer */}
            <div className="border-t border-white/5 p-4 space-y-4 bg-black/50">
              {/* TVA 10% Promotion Banner */}
              {!hasInstallation && (
                <div className="bg-emerald-500/5 border border-fgs-primary/20 rounded-xl p-3">
                  <div className="flex items-start gap-3">
                    <Wrench className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium">TVA à 10% avec installation</p>
                      <p className="text-xs text-white/60 mt-0.5">
                        Économisez sur votre commande !
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  </div>
                </div>
              )}

              {/* TVA 10% Applied Badge */}
              {hasInstallation && installationConfirmed && (
                <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-2 text-center">
                  <span className="text-green-400 text-sm font-medium flex items-center justify-center gap-2">
                    <Percent className="w-4 h-4" />
                    TVA 10% appliquée
                  </span>
                </div>
              )}

              {/* Promo Code Section */}
              {cart.promo_code ? (
                <div className="bg-emerald-500/5 border border-fgs-primary/20 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-emerald-400" />
                      <span className="text-white font-medium text-sm">{cart.promo_code}</span>
                      <span className="text-emerald-400 font-mono text-sm">
                        -{cart.promo_discount?.toFixed(2)} €
                      </span>
                    </div>
                    <button
                      onClick={handleRemovePromo}
                      className="p-1 text-white/60 hover:text-red-400 transition-colors"
                      title="Retirer le code promo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    placeholder="Code promo"
                    className="flex-1 bg-black border-white/10 text-white text-sm h-9 placeholder:text-white/50"
                    onKeyPress={(e) => e.key === 'Enter' && handleApplyPromo()}
                    data-testid="promo-code-input"
                  />
                  <Button
                    onClick={handleApplyPromo}
                    disabled={applyingPromo || !promoInput.trim()}
                    variant="outline"
                    className="border-white/10 text-white hover:bg-white/5 h-9 px-3"
                    data-testid="apply-promo-btn"
                  >
                    {applyingPromo ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Tag className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              )}

              {/* Price Breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-white/60">Sous-total HT</span>
                  <span className="text-white font-mono">
                    {(cart.total_ht ?? (cart.total / 1.20)).toFixed(2)} €
                  </span>
                </div>
                
                {/* TVA Display - handle mixed rates */}
                {cart.tva_breakdown && cart.tva_breakdown.subscriptions_4g?.base_ht > 0 ? (
                  // Mixed TVA: products at 10% or 20%, subscriptions always at 20%
                  <>
                    {cart.tva_breakdown.products?.base_ht > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-white/60">
                          TVA {cart.tva_breakdown.products.rate}% (produits)
                        </span>
                        <span className={`font-mono ${cart.tva_breakdown.products.rate === 10 ? 'text-green-400' : 'text-white'}`}>
                          {cart.tva_breakdown.products.amount?.toFixed(2)} €
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">
                        TVA 20% (forfait 4G)
                      </span>
                      <span className="text-white font-mono">
                        {cart.tva_breakdown.subscriptions_4g.amount?.toFixed(2)} €
                      </span>
                    </div>
                  </>
                ) : (
                  // Single TVA rate
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">
                      TVA {typeof tvaRate === 'number' ? `${tvaRate}%` : '20%'}
                    </span>
                    <span className={`font-mono ${tvaRate === 10 ? 'text-green-400' : 'text-white'}`}>
                      {(cart.tva_amount ?? (cart.total - cart.total / 1.20)).toFixed(2)} €
                    </span>
                  </div>
                )}
                
                {/* Promo Discount Display */}
                {cart.promo_discount > 0 && (
                  <>
                    <div className="flex items-center justify-between text-white/60">
                      <span>Sous-total TTC</span>
                      <span className="font-mono line-through">
                        {cart.subtotal_before_promo?.toFixed(2)} €
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-emerald-400">
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        Réduction ({cart.promo_code})
                      </span>
                      <span className="font-mono">
                        -{cart.promo_discount?.toFixed(2)} €
                      </span>
                    </div>
                  </>
                )}
                
                <div className="flex items-center justify-between pt-2 border-t border-white/5">
                  <span className="text-white font-semibold">Total TTC</span>
                  <span className="text-emerald-400 font-mono text-lg font-bold">
                    {cart.total?.toFixed(2)} €
                  </span>
                </div>
              </div>

              {/* SPLIT PAYMENT DETAILS - Show when cart has subscriptions */}
              {(() => {
                const hasSubscriptions = cart.items.some(item => 
                  item.services?.some(s => s.price_type === 'monthly' || s.price_type === 'annual')
                );
                
                if (!hasSubscriptions) return null;
                
                // Calculate products total (one-time)
                const productsTotal = cart.items.reduce((sum, item) => {
                  const productPrice = (item.price || 0) * (item.quantity || 1);
                  const accessoriesPrice = item.accessories?.reduce((acc, a) => acc + (a.price || 0) * (a.quantity || 1), 0) || 0;
                  const oneTimeServices = item.services?.filter(s => s.price_type === 'one_time' || !s.price_type)
                    .reduce((acc, s) => acc + (s.price || 0), 0) || 0;
                  return sum + productPrice + accessoriesPrice + oneTimeServices;
                }, 0);
                
                // Calculate subscription total
                const subscriptionTotal = cart.items.reduce((sum, item) => {
                  const monthlyServices = item.services?.filter(s => s.price_type === 'monthly' || s.price_type === 'annual')
                    .reduce((acc, s) => acc + (s.price || 0), 0) || 0;
                  return sum + monthlyServices;
                }, 0);
                
                // Get subscription period
                const subscriptionPeriod = cart.items.some(item => 
                  item.services?.some(s => s.price_type === 'annual')
                ) ? '/an' : '/mois';
                
                return (
                  <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 space-y-3">
                    <p className="text-xs text-blue-400 font-medium flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Paiement en 2 étapes
                    </p>
                    
                    {/* Payment 1: Products */}
                    <div className="bg-black/30 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white/60 text-sm">1. Produits & Installation</span>
                        <span className="text-white font-mono font-semibold">{productsTotal.toFixed(2)} €</span>
                      </div>
                      <p className="text-xs text-white/40">CB, Klarna 3x ou SEPA • Paiement immédiat</p>
                    </div>
                    
                    {/* Payment 2: Subscription */}
                    <div className="bg-black/30 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white/60 text-sm">2. Abonnement 4G</span>
                        <div className="text-right">
                          <span className="text-blue-400 font-mono font-semibold">{subscriptionTotal.toFixed(2)} €</span>
                          <span className="text-white/40 text-xs">{subscriptionPeriod}</span>
                        </div>
                      </div>
                      <p className="text-xs text-white/40">Prélèvement SEPA uniquement • Configuration après achat</p>
                    </div>
                  </div>
                );
              })()}

              {/* Shipping Notice */}
              <p className="text-xs text-white/50 text-center">
                Livraison gratuite dès 250€ d'achat
              </p>

              {/* Klarna Payment Option - Only for one-time payments (no subscriptions) */}
              {(() => {
                // Check if cart has subscriptions (monthly/annual services)
                const hasSubscriptions = cart.items.some(item => 
                  item.services?.some(s => s.price_type === 'monthly' || s.price_type === 'annual')
                );
                
                // Calculate products-only total (excluding subscriptions)
                const productsTotal = cart.items.reduce((sum, item) => {
                  const productPrice = (item.price || 0) * (item.quantity || 1);
                  const accessoriesPrice = item.accessories?.reduce((acc, a) => acc + (a.price || 0) * (a.quantity || 1), 0) || 0;
                  // Only add one-time services (not monthly/annual)
                  const oneTimeServices = item.services?.filter(s => s.price_type === 'one_time' || !s.price_type)
                    .reduce((acc, s) => acc + (s.price || 0), 0) || 0;
                  return sum + productPrice + accessoriesPrice + oneTimeServices;
                }, 0);
                
                // Show Klarna only if no subscriptions and products total >= 35€
                if (hasSubscriptions || productsTotal < 35) return null;
                
                return (
                  <div className="bg-[#FFB3C7]/10 border border-[#FFB3C7]/20 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#FFB3C7] rounded-lg flex items-center justify-center">
                          <span className="text-black font-black text-xs">K.</span>
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">Payer en 3x avec Klarna</p>
                          <p className="text-white/50 text-xs">Sans frais • Disponible au paiement</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[#FFB3C7] font-mono font-bold">{(productsTotal / 3).toFixed(2)} €</p>
                        <p className="text-white/40 text-xs">/mois × 3</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Checkout Button */}
              <Button 
                onClick={handleCheckout}
                disabled={loading}
                className="w-full bg-white text-black hover:bg-white/90 h-12 text-base font-medium rounded-full"
                data-testid="checkout-button"
              >
                Passer commande
              </Button>

              {/* Continue Shopping */}
              <button 
                onClick={() => setCartOpen(false)}
                className="w-full text-center text-sm text-white/60 hover:text-white transition-colors py-2"
              >
                Continuer mes achats
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
